import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, Loader2, ShieldAlert, CalendarClock, PlayCircle, PauseCircle, MessageSquare } from "lucide-react";
import api from "@/lib/api";

/**
 * Página de configuração do "go-live" (18/02/2026).
 *
 * A data de arranque controla se o sistema classifica automaticamente
 * (categorias Outlook, notas Zoho, auto-perdido, payment references) ou
 * fica em modo "sync-only". Antes da data o Zoho continua a sincronizar
 * mas nada do lado Caring é escrito.
 *
 * A acção "Limpar dados" (destrutiva) faz backup e depois apaga:
 *   - Campos operacionais em TODOS os leads
 *   - Colecções auxiliares (email_logs, sync_events, daf_reconciliations…)
 */
export default function SystemSettings() {
  const [state, setState] = useState(null);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeInput, setPurgeInput] = useState("");
  const [purgeResult, setPurgeResult] = useState(null);
  const [bannerEnabled, setBannerEnabled] = useState(true);
  const [bannerText, setBannerText] = useState("");
  const [bannerSaving, setBannerSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const { data } = await api.get("/system/settings");
      setState(data);
      setDate(data.system_start_date || "");
      setBannerEnabled(data.client_banner_enabled !== false);
      setBannerText(data.client_banner_text || "");
    } catch (e) {
      toast.error("Erro ao carregar configuração do sistema");
    }
  };

  const save = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast.error("Data em formato inválido (use YYYY-MM-DD)");
      return;
    }
    setSaving(true);
    try {
      await api.put("/system/settings", { system_start_date: date });
      toast.success("Data de arranque guardada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a guardar");
    } finally { setSaving(false); }
  };

  const saveBanner = async () => {
    if (bannerText.length > 500) {
      toast.error("Texto do banner não pode exceder 500 caracteres");
      return;
    }
    setBannerSaving(true);
    try {
      await api.put("/system/settings", {
        client_banner_enabled: bannerEnabled,
        client_banner_text: bannerText,
      });
      toast.success("Aviso ao cliente guardado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a guardar");
    } finally { setBannerSaving(false); }
  };

  const purge = async () => {
    if (purgeInput !== "PURGE") {
      toast.error("Escreva PURGE em maiúsculas para confirmar");
      return;
    }
    if (!window.confirm("Confirma? Vai apagar TODOS os dados operacionais criados neste sistema (leads mantêm-se, mas propostas, aceitações, RTs, aprovações da Direção Técnica, plano de cuidados etc. são zerados). É gerado um backup em /app/backend/backups/ antes de apagar.")) {
      return;
    }
    setPurging(true);
    setPurgeResult(null);
    try {
      const { data } = await api.post("/system/purge", { confirm: "PURGE" });
      setPurgeResult(data);
      toast.success(`Purga executada · backup em ${data.backup_file}`);
      setPurgeInput("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na purga");
    } finally { setPurging(false); }
  };

  const inp = "bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary/50";

  if (!state) return <div className="p-8 text-sm text-muted-foreground">A carregar…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-2">Go-Live</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Sistema · Data de Arranque</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Enquanto o sistema estiver "pré-arranque" o Zoho continua a sincronizar leads mas o Caring não classifica nada, não escreve notas Zoho automáticas, não corre auto-perdido e não gera referências de pagamento.
        </p>
      </header>

      <section className="bg-card border border-border rounded-2xl p-6 lg:p-8">
        <div className="flex items-start gap-3 mb-4">
          {state.active ? (
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 flex items-center justify-center flex-shrink-0">
              <PlayCircle className="w-5 h-5 text-[hsl(var(--success))]" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 flex items-center justify-center flex-shrink-0">
              <PauseCircle className="w-5 h-5 text-[hsl(var(--warning))]" />
            </div>
          )}
          <div className="flex-1">
            <div className="font-ui text-lg">
              {state.active ? "ACTIVO — automatismos ligados" : "PRÉ-ARRANQUE — automatismos desligados"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {state.active
                ? `O sistema começou a classificar em ${state.system_start_date}.`
                : `Vai arrancar em ${state.system_start_date}. Zoho sync continua a correr até lá.`}
            </div>
          </div>
        </div>

        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">
          <CalendarClock className="w-3 h-3 inline mr-1" /> Data de arranque (YYYY-MM-DD)
        </label>
        <div className="flex items-center gap-3">
          <input
            type="date"
            className={`${inp} flex-1`}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="system-start-date-input"
          />
          <button
            onClick={save}
            disabled={saving}
            data-testid="system-start-date-save"
            className="inline-flex items-center gap-2 text-sm bg-foreground hover:bg-slate-800 text-background rounded-full px-5 py-2.5 font-semibold disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>
        {state.updated_at && (
          <div className="text-[10px] text-muted-foreground mt-2">
            Última alteração: {new Date(state.updated_at).toLocaleString("pt-PT")}
          </div>
        )}
      </section>

      <section className="bg-card border border-border rounded-2xl p-6 lg:p-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5 text-[hsl(var(--warning))]" />
          </div>
          <div className="flex-1">
            <div className="font-ui text-lg">Aviso ao cliente durante a transição</div>
            <div className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Banner mostrado em tudo o que o cliente recebe (páginas públicas, emails automáticos, PDFs da proposta e do plano de cuidados). Desligue quando o novo sistema estabilizar.
            </div>
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm mb-3 select-none cursor-pointer">
          <input
            type="checkbox"
            checked={bannerEnabled}
            onChange={(e) => setBannerEnabled(e.target.checked)}
            data-testid="client-banner-enabled-toggle"
            className="w-4 h-4"
          />
          Mostrar aviso ao cliente
        </label>

        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">
          Texto (máx. 500 caracteres)
        </label>
        <textarea
          rows={3}
          className={`${inp} w-full`}
          value={bannerText}
          onChange={(e) => setBannerText(e.target.value)}
          maxLength={500}
          data-testid="client-banner-text-input"
        />
        <div className="text-[10px] text-muted-foreground mt-1">{bannerText.length}/500</div>

        <div className="mt-3">
          <button
            onClick={saveBanner}
            disabled={bannerSaving}
            data-testid="client-banner-save"
            className="inline-flex items-center gap-2 text-sm bg-foreground hover:bg-slate-800 text-background rounded-full px-5 py-2.5 font-semibold disabled:opacity-40"
          >
            {bannerSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar aviso
          </button>
        </div>

        {bannerEnabled && bannerText.trim() && (
          <div className="mt-4 rounded-lg border border-[#FCD9B0] bg-[hsl(var(--warning-bg))] px-4 py-2.5 text-sm text-[#8F4F0A]">
            <span className="font-semibold mr-1">Pré-visualização · Aviso · </span>
            {bannerText}
          </div>
        )}
      </section>

      <section className="bg-card border border-[hsl(var(--danger))]/25 rounded-2xl p-6 lg:p-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-[hsl(var(--danger))]" />
          </div>
          <div>
            <div className="font-ui text-lg text-[hsl(var(--danger))]">Zona destrutiva · Limpar dados</div>
            <div className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Apaga todos os dados operacionais criados neste sistema — leads mantêm-se mas propostas, aceitações, aprovações da Direção Técnica, plano de cuidados, plano de visitas, RTs, referências de pagamento, follow-ups e histórico são zerados. Colecções auxiliares (email_logs, sync_events, daf_reconciliations, zoho_sync_logs, proposal_versions, daily_reports_cache) são apagadas por completo. Um backup completo é criado em <code>/app/backend/backups/</code> antes de apagar.
            </div>
          </div>
        </div>

        <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--danger))] font-semibold block mb-1">
          Escreva <b>PURGE</b> em maiúsculas para desbloquear
        </label>
        <div className="flex items-center gap-3">
          <input
            className={`${inp} flex-1`}
            value={purgeInput}
            onChange={(e) => setPurgeInput(e.target.value)}
            placeholder="PURGE"
            data-testid="purge-confirm-input"
          />
          <button
            onClick={purge}
            disabled={purging || purgeInput !== "PURGE"}
            data-testid="purge-btn"
            className="inline-flex items-center gap-2 text-sm bg-red-600 hover:bg-red-700 text-primary-foreground rounded-full px-5 py-2.5 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Limpar tudo
          </button>
        </div>

        {purgeResult && (
          <div className="mt-4 rounded-lg border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-3 text-xs text-[hsl(var(--success))] space-y-1" data-testid="purge-result">
            <div><b>Backup:</b> <code>{purgeResult.backup_file}</code></div>
            <div><b>Leads actualizados:</b> {purgeResult.leads_updated}</div>
            <div><b>Colecções apagadas:</b> {(purgeResult.collections_dropped || []).join(" · ") || "—"}</div>
          </div>
        )}
      </section>
    </div>
  );
}
